'use client'
import { useState } from 'react'
import { Modal, Popover, message } from 'antd'
import styles from './styles.module.scss'
import Badge from '@/components/Badge'
import Tabs from '@/components/Tabs'
import NoData from '@/components/NoData'
import { useLocale } from 'core/components/Locale'
import { useNotify } from 'core/controller/notify/notify'
import { TPostV2InmailMessageListResponse } from 'core/service/yapi/hk-web/InmailNotice/api'
import MessageItem from './MessageItem'
import { useHCRouter } from '@/components/HCNextLink'
import { LoadingOutlined } from '@ant-design/icons'
import { Spin } from 'antd'

// 定义消息类型
type NotifyMessage = TPostV2InmailMessageListResponse['data'][number]

const Notify = () => {
  const router = useHCRouter()
  const t = useLocale()
  const [openClearAllModal, setOpenClearAllModal] = useState(false)
  const [disabledClearAll, setDisabledClearAll] = useState(false)

  const {
    navList,
    messages,
    activeTabs,
    updateActiveTabs,
    unReadCountTotal,
    isLoading,
    isFetchingMore,
    hasNextPage,
    loadMoreRef,
    hasNewTask,
    markAllAsRead,
    handleMessageClick,
    setSubmoduleId,
    submoduleId,
  } = useNotify({
    navMaps: [
      t('home_v11_All'),
      t('home_v11_Announcement'),
      t('home_v11_System'),
      t('home_v11_Quotes'),
      t('home_v11_Trade'),
    ],
  })

  // 渲染Tab内容
  const renderTab = () => {
    const currentNav = navList[activeTabs[0]]

    return (
      <>
        <Tabs
          activeKey={String(currentNav.value)}
          items={navList.map((i) => ({
            key: String(i.value),
            label: i.label,
          }))}
          onChange={(key) => {
            setSubmoduleId(0)
            updateActiveTabs([navList.findIndex((i) => i.value === Number(key)), 0])
          }}
        />
        {currentNav?.submodule?.length > 0 && (
          <div className={styles.subNav}>
            <span
              className={`${styles.subNavItem} ${submoduleId === 0 ? styles.active : ''}`}
              onClick={() => setSubmoduleId(0)}
            >
              {t('home_v11_All')}
            </span>
            {currentNav.submodule.map((sub) => {
              return (
                <span
                  key={sub.code}
                  className={`${styles.subNavItem} ${sub.code === submoduleId ? styles.active : ''}`}
                  onClick={() => (sub.code !== undefined ? setSubmoduleId(sub.code) : setSubmoduleId(0))}
                >
                  {sub.name}
                </span>
              )
            })}
          </div>
        )}
      </>
    )
  }

  // 新手任务提醒
  const renderNewTask = () => {
    if (activeTabs[0] === 0 && hasNewTask) {
      return (
        <div className={styles.newTask} onClick={() => router.push('/benefitsCenter/home')}>
          <div className={styles.giftIcon}></div>
          <span>{t('home_v11_NewbieTaskReminderComplete')}</span>
        </div>
      )
    }
    return null
  }

  //获取标签
  const findLable = (msg: NotifyMessage) => {
    // 找到对应的主模块
    const currentModule = navList.find((nav) => nav?.code === msg.moduleCode)
    if (!currentModule) return ''

    // 在主模块的子模块中查找对应的子模块名称
    const subModule = currentModule.submodule.find((sub) => sub?.code === msg.submoduleCode)
    return subModule?.name || ''
  }

  // 消息列表
  const renderMessages = () => {
    if (!messages.today.length && !messages.earlier.length) {
      return <NoData />
    }

    return (
      <div className={styles.messageList}>
        {messages.today.length > 0 && (
          <>
            <div className={styles.dateTitle}>{t('home_v11_Today')}</div>
            {messages.today.map((msg) => (
              <MessageItem
                key={msg.id}
                message={msg}
                timeType="today"
                onClick={handleMessageClick}
                submoduleName={findLable(msg)}
              />
            ))}
          </>
        )}

        {messages.earlier.length > 0 && (
          <>
            <div className={styles.dateTitle}>{t('home_v11_Earlier')}</div>
            {messages.earlier.map((msg) => (
              <MessageItem
                key={msg.id}
                message={msg}
                timeType="earlier"
                onClick={handleMessageClick}
                submoduleName={findLable(msg)}
              />
            ))}
          </>
        )}

        {/* 加载更多指示器 */}
        {hasNextPage && (
          <div ref={loadMoreRef} className="flex items-center justify-center gap-2">
            {t('exchange_087')}
            <Spin indicator={<LoadingOutlined spin />} size="small" />
          </div>
        )}
      </div>
    )
  }

  // 清空全部弹窗确认
  const handleClearAll = async () => {
    try {
      setDisabledClearAll(true)
      await markAllAsRead()
      setOpenClearAllModal(false)
      message.success(t('home_v11_MarkAllReadSuccess'))
    } catch {
      message.error(t('home_v11_OperationFailed'))
    } finally {
      setDisabledClearAll(false)
    }
  }

  const content = (
    <div className={styles.notifyContent}>
      {renderTab()}
      <div className={styles.messageListWrap}>
        {renderNewTask()}
        {isLoading ? (
          <div className={styles.loading}>
            Loading... <Spin indicator={<LoadingOutlined spin />} size="small" />
          </div>
        ) : (
          renderMessages()
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.markRead} onClick={() => setOpenClearAllModal(true)}>
          <div className={styles.clearIcon} />
          <span>{t('home_v11_AllRead')}</span>
        </div>
        <div className={styles.more} onClick={() => router.push('/inMail/noticeNavList')}>
          {t('home_v11_SeeMore')}
        </div>
      </div>
    </div>
  )

  return (
    <div className={styles.container}>
      <Popover
        // trigger="click"
        content={content}
        getPopupContainer={(node) => node.parentNode as HTMLElement}
        arrow={false}
        placement="bottomLeft"
      >
        <div className={styles.notifyIcon}>
          <Badge count={unReadCountTotal} offset={Number(unReadCountTotal) > 9 ? [8, -5] : [4, -5]}>
            <i className="iconfont icontixing-24 f20" />
          </Badge>
        </div>
      </Popover>

      {/* 清空确认弹窗 */}
      {openClearAllModal && (
        <Modal
          title={t('home_v11_MarkAllAsRead')}
          open={openClearAllModal}
          onOk={handleClearAll}
          onCancel={() => setOpenClearAllModal(false)}
          okButtonProps={{ loading: disabledClearAll }}
          okText={t('market_023')}
          cancelText={t('person_setting_098')}
        >
          <p>{t('home_v11_OnceYouMarkEverything')}</p>
        </Modal>
      )}
    </div>
  )
}

export default Notify
